import { GoogleGenerativeAI } from '@google/generative-ai';

export async function generateSubtopicsDeepseek(Think, prompt, setAPIError, signal, Actor_Model_apiProvider, Actor_Model_apiModel, Actor_Model_apikey, temperature_val_Actor_Model_apiProvider) {
    setAPIError && setAPIError();
    if (!prompt.trim()) {
        // console.error('[generateSubtopicsDeepseek] Empty prompt provided');
        throw new Error('Research topic cannot be empty');
    }
    if (signal?.aborted) {
        // console.error('[generateSubtopicsDeepseek] Aborted before starting');
        throw new Error('Subtopics generation aborted');
    }

    // console.log('[generateSubtopicsDeepseek] Generating research subtopics...');
    const apiKey = Actor_Model_apikey;
    // console.log('[generateSubtopicsDeepseek] API key length:', apiKey ? apiKey.length : 0);
    if (!apiKey) {
        // console.error('[generateSubtopicsDeepseek] Missing API key');
        throw new Error('DEEPSEEK_API_KEY not found in environment variables');
    }

    const temperature_val = parseFloat(temperature_val_Actor_Model_apiProvider);
    // console.log('[generateSubtopicsDeepseek] Temperature value:', temperature_val);
    const model = Actor_Model_apiModel;
    // console.log('[generateSubtopicsDeepseek] Using model:', model);

    // Improved prompts
    const basePrompt = `Analyze this query and generate around 30 specific research angles: "${prompt}"\n`;

    let fullPrompt;
    if (Think) {
        fullPrompt = basePrompt +
            'If this is a prediction or forecast question (like stock prices, market trends, etc.), ' +
            'include around 30 specific research angles about historical patterns, current factors affecting the prediction, potential indicators, ' +
            'and different timeframes to consider. ' +
            'Provide very short reasoning for each research topic (around 30 subtopics should be provided) but please mention the subtopics very clearly using # to start with but do not number them.' +
            'Start each subtopic using "#". Provide around 30 subtopics starting with "#"';
    } else {
        fullPrompt = basePrompt +
            'If this is a prediction or forecast question (like stock prices, market trends, etc.), ' +
            'include angles about historical patterns, current factors affecting the prediction, potential indicators, ' +
            'and different timeframes to consider. ' +
            'Do not Provide any reasoning for each research topic but Please mention the subtopics very clearly using # to start with.' +
            'Start each subtopic using "#"';
    }

    // console.log('[generateSubtopicsDeepseek] Prompt length:', fullPrompt.length);

    // Retry loop: try until a valid response is obtained or an abort occurs.
    let retryCount = 0;
    while (true) {
        retryCount++;
        // console.log('[generateSubtopicsDeepseek] Attempt #', retryCount);

        try {
            // Custom abortable wrapper for fetch with timeout
            const result = await new Promise((resolve, reject) => {
                setAPIError && setAPIError();

                if (signal?.aborted) {
                    // console.error('[generateSubtopicsDeepseek] Aborted during promise setup');
                    setAPIError && setAPIError('Subtopics generation aborted');
                    reject(new Error('Subtopics generation aborted'));
                    return;
                }

                const timeoutId = setTimeout(() => {
                    if (!signal?.aborted) {
                        // console.error('[generateSubtopicsDeepseek] Request timed out after 15 seconds');
                        setAPIError && setAPIError('Subtopics generation timed out');
                        reject(new Error('DeepSeek request timed out after 15 seconds'));
                    }
                }, 15000);

                // console.log('[generateSubtopicsDeepseek] Sending request to DeepSeek API');
                fetch('https://api.deepseek.com/v1/chat/completions', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${apiKey}`,
                    },
                    body: JSON.stringify({
                        model: model,
                        messages: [{
                            role: "user",
                            content: fullPrompt
                        }],
                        temperature: temperature_val,
                        // max_tokens: 4000
                    }),
                    signal // Use the abort signal
                }).then(response => {
                    clearTimeout(timeoutId);
                    if (!signal?.aborted) {
                        // console.log('[generateSubtopicsDeepseek] Received response from DeepSeek API, status:', response.status);
                        resolve(response);
                    } else {
                        // console.error('[generateSubtopicsDeepseek] Request aborted after response received');
                        reject(new Error('Subtopics generation aborted'));
                    }
                }).catch(error => {
                    clearTimeout(timeoutId);
                    // console.error('[generateSubtopicsDeepseek] Fetch error:', error);
                    // console.error('[generateSubtopicsDeepseek] Fetch error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
                    reject(error);
                });

                if (signal) {
                    signal.addEventListener('abort', () => {
                        // console.error('[generateSubtopicsDeepseek] Received abort signal during API call');
                        setAPIError && setAPIError('Subtopics generation aborted');
                        clearTimeout(timeoutId);
                        reject(new Error('Subtopics generation aborted'));
                    });
                }
            });

            if (signal?.aborted) {
                // console.error('[generateSubtopicsDeepseek] Signal aborted after API call');
                setAPIError && setAPIError('Subtopics generation aborted');
                throw new Error('Subtopics generation aborted');
            }

            if (!result.ok) {
                // Fixed: Need to get the error data before using it
                // console.error('[generateSubtopicsDeepseek] API returned error status:', result.status);
                // console.error('[generateSubtopicsDeepseek] API error status text:', result.statusText);

                const errorData = await result.json().catch((parseError) => {
                    // console.error('[generateSubtopicsDeepseek] Failed to parse error response:', parseError);
                    return {};
                });
                // console.error('[generateSubtopicsDeepseek] API error response:', JSON.stringify(errorData));

                setAPIError && setAPIError(errorData.message || 'Failed to get response from DeepSeek');
                throw new Error(errorData.message || 'Failed to get response from DeepSeek');
            }

            // console.log('[generateSubtopicsDeepseek] Parsing response body');
            const data = await result.json().catch((parseError) => {
                // console.error('[generateSubtopicsDeepseek] Failed to parse response body:', parseError);
                // console.error('[generateSubtopicsDeepseek] Parse error details:', JSON.stringify(parseError, Object.getOwnPropertyNames(parseError)));
                throw new Error('Failed to parse DeepSeek response');
            });

            if (!data.choices || !data.choices[0] || !data.choices[0].message) {
                // console.error('[generateSubtopicsDeepseek] Invalid response format:', JSON.stringify(data));
                throw new Error('Invalid response format from DeepSeek');
            }

            const content = data.choices[0].message.content;
            // console.log('[generateSubtopicsDeepseek] Successfully received content, length:', content.length);

            const formattedContent = content.split('\n')
                .map(line => line.replace(/^\d+[.)]\s*/, '').trim())
                .filter(subtopic => subtopic.length > 3)
                .slice(0, 30)
                .join(', ');

            // console.log('[generateSubtopicsDeepseek] Formatted subtopics, length:', formattedContent.length);
            return formattedContent;

        } catch (error) {
            if (error.name === 'AbortError' || error.message.includes('aborted')) {
                // console.error('[generateSubtopicsDeepseek] Operation aborted:', error.message);
                // console.error('[generateSubtopicsDeepseek] Abort error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
                setAPIError && setAPIError(error.message);
                // console.log('[generateSubtopicsDeepseek] DeepSeek request aborted');
                throw new Error('Subtopics generation aborted');
            }

            // console.error('[generateSubtopicsDeepseek] Error in attempt #' + retryCount + ':', error.message);
            // console.error('[generateSubtopicsDeepseek] Error stack:', error.stack);
            // console.error('[generateSubtopicsDeepseek] Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
            setAPIError && setAPIError(error.message);

            // Wait 2 seconds before retrying
            // console.log('[generateSubtopicsDeepseek] Waiting 2 seconds before retry attempt #' + (retryCount + 1));
            await new Promise(resolve => setTimeout(resolve, 2000));
            // Then retry by continuing the loop
        }
    }
}

export async function generateSubtopicsGemini(Think, prompt, setAPIError, signal, Actor_Model_apiModel, Actor_Model_apikey, temperature_val_Actor_Model_apiProvider) {
    setAPIError && setAPIError();
    if (!prompt.trim()) {
        // console.error('[generateSubtopicsGemini] Empty prompt provided');
        throw new Error('Research topic cannot be empty');
    }
    if (signal?.aborted) {
        // console.error('[generateSubtopicsGemini] Aborted before starting');
        throw new Error('Subtopics generation aborted');
    }

    // console.log('[generateSubtopicsGemini] Generating research subtopics...');

    // Improved prompts
    const basePrompt = `Analyze this query and generate around 30 specific research angles: "${prompt}"\n`;

    let fullPrompt;
    if (Think) {
        fullPrompt = basePrompt +
            'If this is a prediction or forecast question (like stock prices, market trends, etc.), ' +
            'include around 30 specific research angles about historical patterns, current factors affecting the prediction, potential indicators, ' +
            'and different timeframes to consider. ' +
            'Provide very short reasoning for each research topic (around 30 subtopics should be provided) but please mention the subtopics very clearly using # to start with but do not number them.' +
            'Start each subtopic using "#". Provide around 30 subtopics starting with "#"';
    } else {
        fullPrompt = basePrompt +
            'If this is a prediction or forecast question (like stock prices, market trends, etc.), ' +
            'include angles about historical patterns, current factors affecting the prediction, potential indicators, ' +
            'and different timeframes to consider. ' +
            'Do not Provide any reasoning for each research topic but Please mention the subtopics very clearly using # to start with.' +
            'Start each subtopic using "#"';
    }

    // console.log('[generateSubtopicsGemini] Prompt length:', fullPrompt.length);

    const apiKey = Actor_Model_apikey;
    // console.log('[generateSubtopicsGemini] API key length:', apiKey ? apiKey.length : 0);
    if (!apiKey) {
        // console.error('[generateSubtopicsGemini] Missing API key');
        throw new Error('GEMINI_API_KEY not found in environment variables');
    }

    // console.log('[generateSubtopicsGemini] Initializing GoogleGenerativeAI');
    const genAI = new GoogleGenerativeAI(apiKey);
    const temperature_val = parseFloat(temperature_val_Actor_Model_apiProvider || "0.7");
    // console.log('[generateSubtopicsGemini] Temperature value:', temperature_val);

    const model = genAI.getGenerativeModel({
        model: Actor_Model_apiModel,
        generationConfig: {
            temperature: temperature_val
        }
    });
    // console.log('[generateSubtopicsGemini] Using model:', Actor_Model_apiModel);

    // Retry loop: try until a valid response is obtained or an abort occurs.
    let retryCount = 0;
    while (true) {
        retryCount++;
        // console.log('[generateSubtopicsGemini] Attempt #', retryCount);

        try {
            // Custom abortable wrapper for generateContent
            const result = await new Promise((resolve, reject) => {
                if (signal?.aborted) {
                    // console.error('[generateSubtopicsGemini] Aborted during promise setup');
                    setAPIError && setAPIError('Subtopics generation aborted');
                    reject(new Error('Subtopics generation aborted'));
                    return;
                }

                const timeoutId = setTimeout(() => {
                    if (!signal?.aborted) {
                        // console.error('[generateSubtopicsGemini] Request timed out after 15 seconds');
                        setAPIError && setAPIError('Subtopics generation timed out');
                        reject(new Error('Gemini request timed out after 15 seconds'));
                    }
                }, 15000);

                // console.log('[generateSubtopicsGemini] Sending request to Gemini API');
                model.generateContent(fullPrompt).then(result => {
                    clearTimeout(timeoutId);
                    if (!signal?.aborted) {
                        // console.log('[generateSubtopicsGemini] Received response from Gemini API');
                        resolve(result);
                    } else {
                        // console.error('[generateSubtopicsGemini] Request aborted after response received');
                        reject(new Error('Subtopics generation aborted'));
                    }
                }).catch(error => {
                    clearTimeout(timeoutId);
                    // console.error('[generateSubtopicsGemini] API error:', error);
                    // console.error('[generateSubtopicsGemini] API error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
                    reject(error);
                });

                if (signal) {
                    signal.addEventListener('abort', () => {
                        // console.error('[generateSubtopicsGemini] Received abort signal during API call');
                        setAPIError && setAPIError('Subtopics generation aborted');
                        clearTimeout(timeoutId);
                        reject(new Error('Subtopics generation aborted'));
                    });
                }
            });

            if (signal?.aborted) {
                // console.error('[generateSubtopicsGemini] Signal aborted after API call');
                setAPIError && setAPIError('Subtopics generation aborted');
                throw new Error('Subtopics generation aborted');
            }

            // console.log('[generateSubtopicsGemini] Extracting text from response');
            const responseText = await result.response.text();
            // console.log('[generateSubtopicsGemini] Response text length:', responseText.length);

            // Parse the generated response to extract subtopics
            const subtopics = [];
            const lines = responseText.split('\n');
            // console.log('[generateSubtopicsGemini] Response has', lines.length, 'lines');

            for (const line of lines) {
                const cleanedLine = line.trim();
                if (!cleanedLine) continue;

                // Try multiple extraction methods
                let subtopic = '';

                // Method 1: Try to extract from numbered/bulleted lists
                const listMatch = cleanedLine.match(/^\s*(\d+[\.\)]|\*|\-)\s*(.+)$/);
                if (listMatch && listMatch[2]) {
                    subtopic = listMatch[2].trim();
                }
                // Method 2: Try to split by delimiters
                else {
                    let delimiter = null;
                    if (cleanedLine.includes('.')) {
                        delimiter = '.';
                    } else if (cleanedLine.includes(')')) {
                        delimiter = ')';
                    }

                    if (delimiter) {
                        const parts = cleanedLine.split(delimiter);
                        if (parts.length > 1) {
                            // Check if the first part looks like a number
                            const firstPart = parts[0].trim();
                            if (/^\d+$/.test(firstPart)) {
                                subtopic = parts[1].trim();
                            }
                        }
                    }
                }

                // Method 3: If no extraction worked and line is meaningful, use the whole line
                if (!subtopic && cleanedLine.length > 3) {
                    subtopic = cleanedLine;
                }

                if (subtopic && subtopic.length > 3) {
                    subtopics.push(subtopic);
                }
            }

            // console.log('[generateSubtopicsGemini] Extracted', subtopics.length, 'subtopics');

            // Remove duplicates while preserving order and select the first 30
            const uniqueSubtopics = Array.from(new Set(subtopics));
            // console.log('[generateSubtopicsGemini] After deduplication:', uniqueSubtopics.length, 'subtopics');

            const result30 = uniqueSubtopics.slice(0, 30).join(", ");
            // console.log('[generateSubtopicsGemini] Final result length:', result30.length);
            return result30;

        } catch (error) {
            if (error.name === 'AbortError' || error.message.includes('aborted')) {
                // console.error('[generateSubtopicsGemini] Operation aborted:', error.message);
                // console.error('[generateSubtopicsGemini] Abort error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
                setAPIError && setAPIError(error.message);
                // console.log('[generateSubtopicsGemini] Gemini request aborted');
                throw new Error('Subtopics generation aborted');
            }

            // console.error('[generateSubtopicsGemini] Error in attempt #' + retryCount + ':', error.message);
            // console.error('[generateSubtopicsGemini] Error stack:', error.stack);
            // console.error('[generateSubtopicsGemini] Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
            setAPIError && setAPIError(error.message);

            // Wait 2 seconds before retrying
            // console.log('[generateSubtopicsGemini] Waiting 2 seconds before retry attempt #' + (retryCount + 1));
            await new Promise(resolve => setTimeout(resolve, 2000));
            // Then retry by continuing the loop
        }
    }
}

export async function generateSubtopicsClaude(Think, prompt, setAPIError, signal, Actor_Model_apiProvider, Actor_Model_apiModel, Actor_Model_apikey, temperature_val_Actor_Model_apiProvider) {
    setAPIError && setAPIError();
    if (!prompt.trim()) {
        // console.error('[generateSubtopicsClaude] Empty prompt provided');
        throw new Error('Research topic cannot be empty');
    }

    if (signal?.aborted) {
        // console.error('[generateSubtopicsClaude] Aborted before starting');
        throw new Error('Subtopics generation aborted');
    }

    // console.log('[generateSubtopicsClaude] Generating research subtopics with Claude...');
    const apiKey = Actor_Model_apikey;
    // console.log('[generateSubtopicsClaude] API key length:', apiKey ? apiKey.length : 0);
    if (!apiKey) {
        // console.error('[generateSubtopicsClaude] Missing API key');
        throw new Error('CLAUDE_API_KEY not found in environment variables');
    }

    // Improved prompts
    const basePrompt = `Analyze this query and generate around 30 specific research angles: "${prompt}"\n`;

    let fullPrompt;
    if (Think) {
        fullPrompt = basePrompt +
            'If this is a prediction or forecast question (like stock prices, market trends, etc.), ' +
            'include around 30 specific research angles about historical patterns, current factors affecting the prediction, potential indicators, ' +
            'and different timeframes to consider. ' +
            'Provide very short reasoning for each research topic (around 30 subtopics should be provided) but please mention the subtopics very clearly using # to start with but do not number them.' +
            'Start each subtopic using "#". Provide around 30 subtopics starting with "#"';
    } else {
        fullPrompt = basePrompt +
            'If this is a prediction or forecast question (like stock prices, market trends, etc.), ' +
            'include angles about historical patterns, current factors affecting the prediction, potential indicators, ' +
            'and different timeframes to consider. ' +
            'Do not Provide any reasoning for each research topic but Please mention the subtopics very clearly using # to start with.' +
            'Start each subtopic using "#"';
    }

    // console.log('[generateSubtopicsClaude] Prompt length:', fullPrompt.length);

    const model = Actor_Model_apiModel;
    // console.log('[generateSubtopicsClaude] Using model:', model);
    const temperature_val = parseFloat(temperature_val_Actor_Model_apiProvider || 0.7);
    // console.log('[generateSubtopicsClaude] Temperature value:', temperature_val);

    // Retry loop: try until a valid response is obtained or an abort occurs.
    let retryCount = 0;
    while (true) {
        retryCount++;
        // console.log('[generateSubtopicsClaude] Attempt #', retryCount);

        try {
            // Custom abortable wrapper for fetch with timeout
            const result = await new Promise((resolve, reject) => {
                setAPIError && setAPIError();

                if (signal?.aborted) {
                    // console.error('[generateSubtopicsClaude] Aborted during promise setup');
                    setAPIError && setAPIError('Subtopics generation aborted');
                    reject(new Error('Subtopics generation aborted'));
                    return;
                }

                const timeoutId = setTimeout(() => {
                    if (!signal?.aborted) {
                        // console.error('[generateSubtopicsClaude] Request timed out after 15 seconds');
                        setAPIError && setAPIError('Subtopics generation timed out');
                        reject(new Error('Claude request timed out after 15 seconds'));
                    }
                }, 15000);

                // Create the request options and body
                const requestBody = JSON.stringify({
                    model: model,
                    messages: [{
                        role: "user",
                        content: fullPrompt
                    }],
                    temperature: temperature_val,
                    max_tokens: 4000
                });

                // console.log('[generateSubtopicsClaude] Preparing request to Claude proxy');

                // Using the proxy server approach as requested
                fetch('https://claude-api-proxy-server.onrender.com/api/claude-proxy', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': apiKey,
                        'anthropic-version': '2023-06-01'
                    },
                    body: requestBody,
                    signal // Use the abort signal
                }).then(response => {
                    clearTimeout(timeoutId);
                    if (!signal?.aborted) {
                        // console.log('[generateSubtopicsClaude] Received response from Claude proxy, status:', response.status);
                        resolve(response);
                    } else {
                        // console.error('[generateSubtopicsClaude] Request aborted after response received');
                        reject(new Error('Subtopics generation aborted'));
                    }
                }).catch(error => {
                    clearTimeout(timeoutId);
                    // console.error("[generateSubtopicsClaude] Claude API Error:", error);
                    // console.error('[generateSubtopicsClaude] Fetch error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
                    reject(error);
                });

                if (signal) {
                    signal.addEventListener('abort', () => {
                        // console.error('[generateSubtopicsClaude] Received abort signal during API call');
                        setAPIError && setAPIError('Subtopics generation aborted');
                        clearTimeout(timeoutId);
                        reject(new Error('Subtopics generation aborted'));
                    });
                }
            });

            if (signal?.aborted) {
                // console.error('[generateSubtopicsClaude] Signal aborted after API call');
                setAPIError && setAPIError('Subtopics generation aborted');
                throw new Error('Subtopics generation aborted');
            }

            if (!result.ok) {
                // console.error('[generateSubtopicsClaude] API returned error status:', result.status);
                // console.error('[generateSubtopicsClaude] API error status text:', result.statusText);

                const errorData = await result.json().catch((parseError) => {
                    // console.error('[generateSubtopicsClaude] Failed to parse error response:', parseError);
                    // console.error('[generateSubtopicsClaude] Parse error details:', JSON.stringify(parseError, Object.getOwnPropertyNames(parseError)));
                    return {};
                });
                console.error("[generateSubtopicsClaude] Claude API Error Response:", JSON.stringify(errorData));
                setAPIError && setAPIError(errorData.error || 'Failed to get response from Claude');
                throw new Error(errorData.error || 'Failed to get response from Claude');
            }

            // console.log('[generateSubtopicsClaude] Parsing response body');
            const data = await result.json().catch(error => {
                // console.error("[generateSubtopicsClaude] Error parsing Claude response:", error);
                // console.error('[generateSubtopicsClaude] Parse error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
                throw new Error('Failed to parse Claude response');
            });

            // console.log("[generateSubtopicsClaude] Claude API Success Response structure:", Object.keys(data));

            // Handle different possible response formats from the proxy
            let content = "";
            if (data.content) {
                content = data.content;
                // console.log('[generateSubtopicsClaude] Found content directly in response');
            } else if (data.choices && data.choices[0] && data.choices[0].message) {
                content = data.choices[0].message.content;
                // console.log('[generateSubtopicsClaude] Found content in choices[0].message');
            } else if (data.response && typeof data.response === 'string') {
                content = data.response;
                // console.log('[generateSubtopicsClaude] Found content in response field');
            }

            if (!content) {
                // console.error('[generateSubtopicsClaude] No content found in response:', JSON.stringify(data));
                throw new Error('No content found in Claude response');
            }

            // console.log('[generateSubtopicsClaude] Successfully received content, length:', content.length);

            const formattedContent = content.split('\n')
                .map(line => line.replace(/^\d+[.)]\s*/, '').trim())
                .filter(subtopic => subtopic.length > 3)
                .slice(0, 30)
                .join(', ');

            // console.log('[generateSubtopicsClaude] Formatted subtopics, length:', formattedContent.length);
            return formattedContent;

        } catch (error) {
            // console.error("[generateSubtopicsClaude] Claude API Error:", error);
            // console.error('[generateSubtopicsClaude] Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));

            if (error.name === 'AbortError' || error.message.includes('aborted')) {
                // console.error('[generateSubtopicsClaude] Operation aborted:', error.message);
                setAPIError && setAPIError(error.message);
                // console.log('[generateSubtopicsClaude] Claude request aborted');
                throw new Error('Subtopics generation aborted');
            }

            // console.error('[generateSubtopicsClaude] Error in attempt #' + retryCount + ':', error.message);
            // console.error('[generateSubtopicsClaude] Error stack:', error.stack);
            setAPIError && setAPIError(error.message);

            // Wait 2 seconds before retrying
            // console.log('[generateSubtopicsClaude] Waiting 2 seconds before retry attempt #' + (retryCount + 1));
            await new Promise(resolve => setTimeout(resolve, 2000));
            // Then retry by continuing the loop
        }
    }
}